import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Descriptions,
  FormInstance,
  Row,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import { ColumnType } from "antd/lib/table";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import entitlements from "../config/entitlements";
import {
  FormFieldConfig,
  Provider,
  ProviderProduct,
  Product,
  SelectOptionsInterface,
} from "../helpers/shared-interfaces";
import {
  getTransactionFieldValue,
  handleValidationErrors,
  injectKey,
  request,
} from "../helpers/utilities";
import {
  GET_PROVIDER,
  ADD_PRODUCT_TO_PROVIDER,
  REMOVE_PRODUCT_FROM_PROVIDER,
  LOAN_DOCUMENTS,
} from "../gql/loans";
import { SEARCH_PRODUCTS } from "../gql/products";
import * as Sentry from "@sentry/browser";
import { RequiredDocumentsDisplay } from "../components/RequiredDocumentsDisplay";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isLoading: boolean;
  id: string;
  showDialog: boolean;
  provider: Provider | null;
  showAddProductModal: boolean;
  isAddingProduct: boolean;
  products: SelectOptionsInterface[];
  showRemoveProductModal: boolean;
  selectedProviderProduct: ProviderProduct | undefined;
  isSearchingProducts: boolean;
  loanDocuments: Array<{ id: string; name: string; }>;
  selectedProduct: any | null;
}

interface RouteParams {
  id: string;
}

const formatTransactionFields = (
  transactionFields: any,
): JSX.Element | null => {
  if (!transactionFields || typeof transactionFields !== "object") {
    return null;
  }

  const fields = [];

  if (transactionFields.interest_per_annum !== undefined) {
    const value = getTransactionFieldValue(
      transactionFields.interest_per_annum,
    );
    fields.push(
      <div key="interest" style={{ marginBottom: 4 }}>
        <Tag color="blue">Interest: {value}%</Tag>
      </div>,
    );
  }

  if (transactionFields.max_amount !== undefined) {
    const value = getTransactionFieldValue(transactionFields.max_amount);
    fields.push(
      <div key="maxAmount" style={{ marginBottom: 4 }}>
        <Tag color="blue">Max: GHS {value.toLocaleString()}</Tag>
      </div>,
    );
  }

  if (transactionFields.min_amount !== undefined) {
    const value = getTransactionFieldValue(transactionFields.min_amount);
    fields.push(
      <div key="minAmount" style={{ marginBottom: 4 }}>
        <Tag color="blue">Min: GHS {value.toLocaleString()}</Tag>
      </div>,
    );
  }

  if (transactionFields.max_tenor !== undefined) {
    const value = getTransactionFieldValue(transactionFields.max_tenor);
    fields.push(
      <div key="maxTenor" style={{ marginBottom: 4 }}>
        <Tag color="blue">Max Tenor: {value} months</Tag>
      </div>,
    );
  }

  if (transactionFields.max_balance_percentage_accessible !== undefined) {
    const value = getTransactionFieldValue(
      transactionFields.max_balance_percentage_accessible,
    );
    fields.push(
      <div key="maxBalancePercentageAccessible" style={{ marginBottom: 4 }}>
        <Tag color="blue">Max Balance Accessible: {value}%</Tag>
      </div>,
    );
  }

  if (transactionFields.lien_amount_multiplier !== undefined) {
    const value = getTransactionFieldValue(
      transactionFields.lien_amount_multiplier,
    );
    fields.push(
      <div key="lienAmountMultiplier" style={{ marginBottom: 4 }}>
        <Tag color="blue">Lien Multiplier: {value}</Tag>
      </div>,
    );
  }

  if (transactionFields.premium !== undefined) {
    const value = getTransactionFieldValue(transactionFields.premium);
    fields.push(
      <div key="premium" style={{ marginBottom: 4 }}>
        <Tag color="blue">Premium: GHS {value.toLocaleString()}</Tag>
      </div>,
    );
  }

  return fields.length > 0 ? <Row>{fields}</Row> : null;
};

export function getProviderProductColumns(
  toggleShowRemoveProductModal: (
    providerProduct: ProviderProduct | undefined,
  ) => void,
): ColumnType<ProviderProduct>[] {
  return [
    {
      title: "Product Name",
      dataIndex: "product",
      key: "productName",
      render(product: Product): JSX.Element {
        return <NavLink to={`/products/${product.id}`}>{product.name}</NavLink>;
      },
    },
    {
      title: "Fund Code",
      dataIndex: "product",
      key: "fundCode",
      render(product: Product): JSX.Element {
        return <span>{product.fundCode}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render(isActive: boolean): JSX.Element {
        return (
          <Tag color={isActive ? "green" : "red"}>
            {isActive ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Added Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render(text: string): JSX.Element {
        return <span>{moment(text).format("ll")}</span>;
      },
    },
    {
      title: "Transaction Details",
      dataIndex: "product",
      key: "transactionFields",
      width: "30%",
      render(product: Product): JSX.Element {
        const transactionFields = product.transactionFields;
        const displayFields = formatTransactionFields(transactionFields);

        return (
          displayFields || <span style={{ color: "#999" }}>No details</span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render(text: string, record: ProviderProduct): JSX.Element {
        return record.isActive ? (
          <DeleteOutlined
            onClick={() => toggleShowRemoveProductModal(record)}
          />
        ) : (
          <span>Removed</span>
        );
      },
    },
  ];
}
export function ProviderProductsTableFactory(
  data: ProviderProduct[],
  columns: ColumnType<ProviderProduct>[],
): JSX.Element {
  return (
    <Table
      bordered={true}
      columns={columns}
      dataSource={injectKey<ProviderProduct>(data, "id")}
      pagination={{ pageSize: 10 }}
    />
  );
}

// function DeleteConfirmationModal(
//   showDialog: boolean,
//   deleteProvider: () => void,
//   toggleDeleteConfirmationModal: () => void,
// ): JSX.Element {
//   return (
//     <Modal
//       title="Confirm Deletion Action"
//       visible={showDialog}
//       okText="Yes"
//       okType="danger"
//       cancelButtonProps={{
//         className: "modal-cancel-btn",
//       }}
//       okButtonProps={{
//         className: "modal-confirm-btn",
//       }}
//       onOk={deleteProvider}
//       onCancel={toggleDeleteConfirmationModal}
//     >
//       <div>Are you sure you want to delete this provider?</div>
//     </Modal>
//   );
// }

export const AddProductUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  products: SelectOptionsInterface[];
  isAddingProduct: boolean;
  onSearch: (value: string) => void;
  isSearching: boolean;
  onProductSelect: (productId: string) => void;
  selectedProduct: any | null;
}): JSX.Element => {
  const baseFields: FormFieldConfig[] = [
    {
      id: "productId",
      name: "productId",
      required: true,
      inputType: "search",
      rule: "string",
      label: "Product",
      placeholder: "Search for a product",
      selectOptions: props.products,
      changeHandler: props.onSearch,
      selectHandler: props.onProductSelect,
      isSearching: props.isSearching,
      colConfig: {
        span: 24,
      },
    },
  ];

  const transactionFields: FormFieldConfig[] = [
    {
      id: "interestPerAnnum",
      name: "interestPerAnnum",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Interest Per Annum (%)",
      placeholder: "Enter interest rate",
      colConfig: {
        span: 10,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "commissionFee",
      name: "commissionFee",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Commission Fee",
      placeholder: "Enter commission fee",
      colConfig: {
        span: 10,
        offset: 1,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "processingFee",
      name: "processingFee",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Processing Fee",
      placeholder: "Enter processing fee",
      colConfig: {
        span: 10,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "insuranceFee",
      name: "insuranceFee",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Insurance Fee",
      placeholder: "Enter insurance fee",
      colConfig: {
        span: 10,
        offset: 1,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "minAmount",
      name: "minAmount",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Minimum Amount",
      placeholder: "Enter minimum amount",
      colConfig: {
        span: 10,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "maxAmount",
      name: "maxAmount",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Maximum Amount",
      placeholder: "Enter maximum amount",
      colConfig: {
        span: 10,
        offset: 1,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "maxTenor",
      name: "maxTenor",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Maximum Tenor (months)",
      placeholder: "Enter maximum tenor",
      colConfig: {
        span: 10,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "lienAmountMultiplier",
      name: "lienAmountMultiplier",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Lien Amount Multiplier",
      placeholder: "Enter lien amount multiplier",
      colConfig: {
        span: 10,
        offset: 1,
      },
      elementStyle: {
        width: "100%",
      },
    },
    {
      id: "maxBalancePercentageAccessible",
      name: "maxBalancePercentageAccessible",
      required: false,
      inputType: "number",
      rule: "number",
      label: "Maximum Balance Percentage Accessible",
      placeholder: "Enter maximum balance percentage accessible",
      colConfig: {
        span: 10,
      },
      elementStyle: {
        width: "100%",
      },
    },
  ];

  // const insuranceFields: FormFieldConfig[] = [
  //   {
  //     id: "premium",
  //     name: "premium",
  //     required: false,
  //     inputType: "number",
  //     rule: "number",
  //     label: "Premium",
  //     placeholder: "Enter premium amount",
  //     colConfig: {
  //       span: 24,
  //     },
  //   },
  // ];

  let fields = [...baseFields];

  if (props.selectedProduct) {
    fields = [...fields, ...transactionFields];
  }

  return (
    <Spin spinning={props.isAddingProduct}>
      <BaseForm
        layout="vertical"
        reference={props.reference}
        fieldsToHide={[]}
        fields={fields}
      />
    </Spin>
  );
};

export default class ViewProvider extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  addProductReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      provider: null,
      isLoading: true,
      id: "",
      showDialog: false,
      showAddProductModal: false,
      isAddingProduct: false,
      products: [],
      showRemoveProductModal: false,
      selectedProviderProduct: undefined,
      isSearchingProducts: false,
      loanDocuments: [],
      selectedProduct: null,
    };
    // this.deleteProvider = this.deleteProvider.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.getProviderData = this.getProviderData.bind(this);
    this.addProductReference = React.createRef<FormInstance>();
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  toggleShowRemoveProductModal = (
    providerProduct: ProviderProduct | undefined,
  ): void => {
    this.setState((prevState) => ({
      selectedProviderProduct: providerProduct,
      showRemoveProductModal: !prevState.showRemoveProductModal,
    }));
  };

  toggleAddProductModal = (): void => {
    this.setState((prevState) => ({
      showAddProductModal: !prevState.showAddProductModal,
      selectedProduct: null, // Reset selected product when modal closes
    }));

    if (this.state.showAddProductModal && this.addProductReference.current) {
      this.addProductReference.current.resetFields();
    }
  };

  redirectToPage = (path: string): void => {
    this.props.history.push(path);
  };

  fetchLoanDocuments = async (): Promise<void> => {
    try {
      const response: AxiosResponse = await request({
        query: LOAN_DOCUMENTS,
      });

      const { data } = response.data;

      if (data?.loanDocuments) {
        this.setState({ loanDocuments: data.loanDocuments });
      }
    } catch (error) {
      message.error(`Failed to fetch loan documents - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  getDocumentName = (documentKey: string): string => {
    const { loanDocuments } = this.state;
    const document = loanDocuments.find((doc) => doc.id === documentKey);
    return document ? document.name : documentKey;
  };

  getProviderData(): void {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_PROVIDER,
          variables: { id },
        })
          .then(({ data }: AxiosResponse<any>): void => {
            this.setState({ isLoading: false });
            if (!data.errors) {
              this.setState({
                provider: data.data.getProvider,
                products: data.data.getProvider.activeProducts,
              });
            } else {
              message.error("Failed to get provider data");
            }
          })
          .catch((error) => {
            this.setState({ isLoading: false });
            message.error("Failed to get provider data");
            Sentry.captureException(error);
          });
      },
    );
  }

  //   deleteProvider(): void {
  //     const { id } = this.state;

  //     this.setState(
  //       {
  //         isLoading: true,
  //       },
  //       (): void => {
  //         request({
  //           query: DELETE_PROVIDER,
  //           variables: { id },
  //         })
  //           .then(({ data }: AxiosResponse<any>): void => {
  //             this.setState({ isLoading: false, showDialog: false });
  //             if (!data.errors && data.data.deleteProvider.result) {
  //               message.success("Provider deleted successfully");
  //               this.redirectToPage("/providers");
  //             } else {
  //               message.error("Failed to delete provider");
  //             }
  //           })
  //           .catch((error): void => {
  //             this.setState({ isLoading: false, showDialog: false });
  //             message.error("Failed to delete provider");
  //             Sentry.captureException(error);
  //           });
  //       },
  //     );
  //   }

  searchProducts = async (searchTerm: string): Promise<void> => {
    if (!searchTerm || searchTerm.length < 2) {
      this.setState({ products: [] });
      return;
    }

    this.setState({ isSearchingProducts: true });

    try {
      const response: AxiosResponse = await request({
        query: SEARCH_PRODUCTS,
        variables: { q: searchTerm, page: 1 },
      });

      const { data } = response.data;

      if (data?.products?.results) {
        const productOptions = data.products.results.map((product: any) => ({
          id: product.id,
          name: `${product.name} (${product.fundCode || "N/A"})`,
          value: product.id,
          rawProduct: product, // Store the full product object
        }));
        this.setState({ products: productOptions });
      }
    } catch (error) {
      message.error(`Failed to search products - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSearchingProducts: false });
    }
  };

  handleProductSelect = (productId: string): void => {
    const { products } = this.state;
    const selectedProduct = products.find((p) => p.id === productId);

    if (selectedProduct && selectedProduct) {
      this.setState({ selectedProduct: selectedProduct });
    }
  };

  addProductToProvider = async () => {
    const { id } = this.state;

    interface Response {
      data: {
        addProductToProvider: {
          providerProduct: ProviderProduct;
          errors: string[];
        };
      };
    }

    if (!this.addProductReference.current) return;

    let data: any;

    this.setState({ isAddingProduct: true });

    try {
      data = await this.addProductReference.current.validateFields();
    } catch (error) {
      handleValidationErrors(error);
      this.setState({ isAddingProduct: false });
      return;
    }

    try {
      const {
        productId,
        interestPerAnnum,
        commissionFee,
        processingFee,
        insuranceFee,
        maxAmount,
        maxTenor,
        minAmount,
        maxBalancePercentageAccessible,
        lienAmountMultiplier,
        premium,
      } = data;

      const variables: any = {
        providerId: id,
        productId,
      };

      if (interestPerAnnum !== undefined && interestPerAnnum !== null) {
        variables.interestPerAnnum = interestPerAnnum;
      }
      if (commissionFee !== undefined && commissionFee !== null) {
        variables.commissionFee = commissionFee;
      }
      if (processingFee !== undefined && processingFee !== null) {
        variables.processingFee = processingFee;
      }
      if (insuranceFee !== undefined && insuranceFee !== null) {
        variables.insuranceFee = insuranceFee;
      }
      if (maxAmount !== undefined && maxAmount !== null) {
        variables.maxAmount = maxAmount;
      }
      if (maxTenor !== undefined && maxTenor !== null) {
        variables.maxTenor = maxTenor;
      }
      if (minAmount !== undefined && minAmount !== null) {
        variables.minAmount = minAmount;
      }
      if (premium !== undefined && premium !== null) {
        variables.premium = premium;
      }
      if (
        maxBalancePercentageAccessible !== undefined &&
        maxBalancePercentageAccessible !== null
      ) {
        variables.maxBalancePercentageAccessible =
          maxBalancePercentageAccessible;
      }
      if (lienAmountMultiplier !== undefined && lienAmountMultiplier !== null) {
        variables.lienAmountMultiplier = lienAmountMultiplier;
      }

      const response: AxiosResponse<Response> = await request({
        query: ADD_PRODUCT_TO_PROVIDER,
        variables,
      });

      const { data: responseData } = response.data;

      if (
        responseData.addProductToProvider.providerProduct &&
        responseData.addProductToProvider.errors.length === 0
      ) {
        message.success("Product added successfully");
        this.addProductReference.current.resetFields();
        this.setState({ selectedProduct: null });
        this.getProviderData();
      } else if (responseData.addProductToProvider.errors.length > 0) {
        message.error(
          responseData.addProductToProvider.errors.join(", ") ||
          "Failed to add product",
        );
      } else {
        message.error("An error occurred. Your request couldn't be processed.");
      }
    } catch (error) {
      message.error(`Failed to add product - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isAddingProduct: false });
      this.toggleAddProductModal();
    }
  };

  removeProductFromProvider = async () => {
    const { selectedProviderProduct } = this.state;

    if (!selectedProviderProduct) return;

    interface Response {
      data: {
        removeProductFromProvider: {
          providerProduct: ProviderProduct;
          errors: string[];
        };
      };
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: REMOVE_PRODUCT_FROM_PROVIDER,
        variables: {
          providerProductId: selectedProviderProduct.id,
        },
      });

      const { data: responseData } = response.data;

      if (
        responseData.removeProductFromProvider.providerProduct &&
        responseData.removeProductFromProvider.errors.length === 0
      ) {
        message.success("Product removed successfully");
        this.getProviderData();
      } else if (responseData.removeProductFromProvider.errors.length > 0) {
        message.error(
          responseData.removeProductFromProvider.errors.join(", ") ||
          "Failed to remove product",
        );
      } else {
        message.error("An error occurred. Your request couldn't be processed.");
      }
    } catch (error) {
      message.error(`Failed to remove product - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleShowRemoveProductModal(undefined);
    }
  };

  componentDidMount(): void {
    const { id } = this.props.match.params;

    this.setState({ id }, (): void => {
      this.getProviderData();
      this.fetchLoanDocuments();
    });
  }

  render(): JSX.Element | null {
    const {
      isLoading,
      id,
      provider,
      showAddProductModal,
      isAddingProduct,
      products,
      showRemoveProductModal,
      isSearchingProducts,
      selectedProduct,
    } = this.state;

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            <div>
              {/* {DeleteConfirmationModal(
                showDialog,
                this.deleteProvider,
                this.toggleDeleteConfirmationModal,
              )} */}
              {ModalCreator({
                title: "Add Product to Provider",
                okType: "primary",
                okText: "Add Product",
                showModal: showAddProductModal,
                isLoading: isAddingProduct,
                onOkay: this.addProductToProvider,
                toggleModal: this.toggleAddProductModal,
                inscription: AddProductUI({
                  reference: this.addProductReference,
                  products: products,
                  isAddingProduct: isAddingProduct,
                  onSearch: this.searchProducts,
                  isSearching: isSearchingProducts,
                  onProductSelect: this.handleProductSelect,
                  selectedProduct: selectedProduct,
                }),
              })}
              {ModalCreator({
                title: "Remove Product from Provider",
                okType: "danger",
                okText: "Remove Product",
                showModal: showRemoveProductModal,
                isLoading: isLoading,
                onOkay: this.removeProductFromProvider,
                toggleModal: () => this.toggleShowRemoveProductModal(undefined),
                inscription: (
                  <div>
                    Are you sure you want to remove this product from the
                    provider?
                  </div>
                ),
              })}
              <Descriptions
                title={<div>{provider?.name}</div>}
                extra={
                  <div>
                    <EntitledSection
                      entitlement={entitlements.LoanProviders?.create}
                    >
                      <Button
                        className="base-button"
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={this.toggleAddProductModal}
                        data-cy="test-add-product-button"
                        style={{
                          backgroundColor: "rgb(29, 55, 100)",
                          color: "white",
                          marginRight: "10px",
                        }}
                      >
                        Add Product
                      </Button>
                    </EntitledSection>
                    <EntitledSection
                      entitlement={entitlements.LoanProviders?.edit}
                    >
                      <NavLink to={`/providers/${id}/edit`}>
                        <Button
                          icon={<EditOutlined />}
                          size="large"
                          style={{
                            backgroundColor: "rgb(29, 55, 100)",
                            color: "white",
                            marginRight: "10px",
                          }}
                          data-cy="test-provider-edit-button"
                        >
                          Edit
                        </Button>
                      </NavLink>
                    </EntitledSection>
                    {/* <EntitledSection
                      entitlement={entitlements.RedemptionRequests?.delete}
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={this.toggleDeleteConfirmationModal}
                        data-cy="test-provider-delete-button"
                      >
                        Delete
                      </Button>
                    </EntitledSection> */}
                  </div>
                }
              >
                <Descriptions.Item
                  label={<b>Provider Name</b>}
                  key="name"
                  span={1}
                >
                  {provider?.name}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Contact Email</b>}
                  key="contactEmail"
                  span={1}
                >
                  {provider?.contactEmail || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>CC Emails</b>}
                  key="ccEmails"
                  span={1}
                >
                  {provider?.ccEmails && provider.ccEmails.length > 0 ? (
                    provider.ccEmails.map((email) => (
                      <Tag key={email} color="blue">
                        {email}
                      </Tag>
                    ))
                  ) : (
                    "N/A"
                  )}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Created Date</b>}
                  key="createdAt"
                  span={1}
                >
                  {provider?.createdAt &&
                    moment(provider.createdAt).format("ll")}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<b>Terms &amp; Conditions</b>}
                  key="termsAndConditions"
                  span={1}
                >
                  {provider?.termsAndConditions ? (
                    <Tag color="green">Configured</Tag>
                  ) : (
                    <Tag color="default">Not Configured</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
              <div style={{ marginTop: 24 }}>
                <h3 style={{ marginBottom: 16 }}>
                  Required Documents by Loan Type
                </h3>
                <RequiredDocumentsDisplay
                  provider={provider}
                  getDocumentName={this.getDocumentName}
                />
              </div>
            </div>
            {provider?.activeProviderProducts && (
              <div>
                <p
                  style={{
                    marginTop: "20px",
                    fontSize: "15px",
                  }}
                >
                  <b>Provider Products</b>
                </p>
                {ProviderProductsTableFactory(
                  provider.activeProviderProducts,
                  getProviderProductColumns(this.toggleShowRemoveProductModal),
                )}
              </div>
            )}
          </Fragment>
        </Card>
      </Spin>
    );
  }
}
